import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { observer } from "mobx-react-lite";
import { GitBranch, ListChecks, Plus, Unlink, X } from "lucide-react";
import type { IssueStatus } from "@/domains/pm/types";
import type { IssueDraftChangeMeta, IssueDraftValues } from "@/domains/pm/issueDraft";
import type { PrefilledElement } from "@/domains/pm/createIssueWithAssociations";
import { useIssuesStore, usePMStore, useProjectMembersStore } from "@/stores/pm/hooks/usePMStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useProjectResponsibilities } from "@/domains/contacts/useResponsibilities";
import { useStatusCatalogue } from "@/domains/pm/statuses/statusCatalogueContext";
import { isOpenStatus } from "@/domains/pm/statuses/statusCatalogue";
import { isDateRangeInverted } from "@/domains/pm/utils/issueDates";
import { isDueDateOverdue } from "@/domains/pm/utils/panelDates";
import { ISSUE_ATTACHMENTS_FOLDER_NAME } from "@/domains/documents/services/folderService";
import { DisciplinePicker } from "@/domains/pm/components/shared/DisciplinePicker";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { PanelSectionLabel } from "./PanelSectionLabel";
import { IssueFieldGrid } from "./IssueFieldGrid";
import { ChecklistDraftField } from "./ChecklistDraftField";
import { IssueAttachmentsStager, type StagedAttachment } from "./IssueAttachmentsStager";
import { ParentIssuePickerDialog } from "./ParentIssuePickerDialog";
import { ISSUE_TITLE_TEXTAREA_CLASS, useAutoFitTitle } from "./useAutoFitTitle";
import { isImeEnter, useEnterToSave } from "./enterToSave";

export interface IssueDraftContentProps {
  buildingId: number;
  /**
   * The draft, owned by the caller (dialog state, or `IssuesStore.drafts`). Its
   * `projectId` is the entry point's and is not offered for change: every place
   * a new issue starts from already knows its project.
   */
  values: IssueDraftValues;
  /**
   * Every edit, as a patch of the fields it changed. `meta.correction` marks
   * the form fixing up a default (an assignee without access to the project),
   * not the person's input.
   */
  onChange: (patch: Partial<IssueDraftValues>, meta?: IssueDraftChangeMeta) => void;
  /** Renders the header's close button, as the detail panel's. */
  onClose?: () => void;
  /** The close button is shown but refused — a save or an upload is running. */
  closeDisabled?: boolean;
  /** An upload in the Documents band started or landed. */
  onUploadingChange?: (uploading: boolean) => void;
  /** Header controls placed before the close button — the draft panel's Create. */
  headerActions?: ReactNode;
  /** Below the scrolling body, outside it — the dialog's Cancel / Create row. */
  footer?: ReactNode;
  /** Where an existing issue shows "#N". Defaults to "New issue". */
  headerLabel?: string;
  /** The BIM element the issue is raised from — shown as a "Linking:" chip. */
  prefilledElement?: PrefilledElement;
  /** Focus the description on mount. */
  autoFocusTitle?: boolean;
  /** Bump to focus the description again ("Add description" on the leave warning). */
  titleFocusToken?: number;
  /**
   * Enter in the description (without Shift). The dialog creates the issue; left
   * unset, Enter only leaves the field — the draft panel saves on leave or Create.
   */
  onTitleEnter?: () => void;
  /**
   * Enter anywhere in the New issue while nothing is being typed or picked
   * (see `shouldEnterSave`): the dialog's Create, the draft panel's close —
   * which saves a titled draft. Left unset, Enter does nothing extra.
   */
  onEnterSave?: () => void;
  /** Shown under the fields — a failed save. */
  error?: string | null;
  /** Every control inert — a save is running. */
  disabled?: boolean;
  /** Rendered inside a Radix Dialog, which owns Escape. */
  withinDialog?: boolean;
  className?: string;
}

/**
 * A new issue, drawn as the expanded issue view (IssueDetailContent) draws an
 * existing one — the same header strip, the borderless auto-fitting title in
 * its ruled band, the field grid, Tags, Checklist and Documents bands — so the
 * create dialog and the /issues draft panel look like the issue they become.
 *
 * Controlled and presentational: every value comes from `values` and every edit
 * goes out through `onChange`; nothing is written until the caller creates the
 * issue. The sections that need an issue id (comments, history, dependencies,
 * origin tags) are absent.
 */
export const IssueDraftContent = observer(function IssueDraftContent({
  buildingId,
  values,
  onChange,
  onClose,
  closeDisabled = false,
  onUploadingChange,
  headerActions,
  footer,
  headerLabel,
  prefilledElement,
  autoFocusTitle = true,
  titleFocusToken = 0,
  onTitleEnter,
  onEnterSave,
  error,
  disabled = false,
  withinDialog = false,
  className,
}: IssueDraftContentProps) {
  const { t } = useLanguage();
  const issuesStore = useIssuesStore();
  const pmStore = usePMStore();
  const projectMembers = useProjectMembersStore();
  const { catalogue: statusCatalogue } = useStatusCatalogue();

  // The latest values, ahead of the caller's re-render: an upload resolving
  // between two renders appends to what is staged NOW, not to a stale render's
  // list (see IssueAttachmentsStager.onChange).
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const change = useCallback(
    (patch: Partial<IssueDraftValues>, meta?: IssueDraftChangeMeta) => {
      valuesRef.current = { ...valuesRef.current, ...patch };
      onChange(patch, meta);
    },
    [onChange]
  );
  const setStagedAttachments: Dispatch<SetStateAction<StagedAttachment[]>> = (action) =>
    change({
      stagedAttachments:
        typeof action === "function" ? action(valuesRef.current.stagedAttachments) : action,
    });

  const projectId = values.projectId;

  // ── Title ──────────────────────────────────────────────────────────────
  const titleRef = useRef<HTMLTextAreaElement>(null);
  useAutoFitTitle(titleRef, values.title);
  useEffect(() => {
    if (!autoFocusTitle && titleFocusToken === 0) return;
    // A frame later: a host dialog or panel settles its own focus first.
    const frame = requestAnimationFrame(() => {
      const ta = titleRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(ta.value.length, ta.value.length);
    });
    return () => cancelAnimationFrame(frame);
  }, [autoFocusTitle, titleFocusToken]);

  // ── Enter saves ────────────────────────────────────────────────────────
  const rootRef = useRef<HTMLDivElement>(null);
  useEnterToSave(rootRef, onEnterSave, !disabled);

  // ── Header "+" menu ────────────────────────────────────────────────────
  const [checklistFocusToken, setChecklistFocusToken] = useState(0);
  const [parentPickerOpen, setParentPickerOpen] = useState(false);
  // Radix hands focus back to the "+" trigger as the menu closes, which would
  // blur the just-opened checklist composer and put the empty checklist away.
  const keepFocusOnClose = useRef(false);
  const canAddChecklist = values.checklistItems === null;
  const canMarkSubissue = values.parentIssueId == null;
  const openChecklist = () => {
    keepFocusOnClose.current = true;
    change({ checklistItems: [] });
    setChecklistFocusToken((n) => n + 1);
  };

  const parentIssue =
    values.parentIssueId != null
      ? (issuesStore.issues.find((i) => i.id === values.parentIssueId) ?? null)
      : null;

  // ── People, disciplines ────────────────────────────────────────────────
  useEffect(() => {
    pmStore.loadDisciplines();
  }, [pmStore]);

  // WHA-3507: the assignee picker is scoped to the project's members.
  useEffect(() => {
    if (projectId != null && projectId > 0) projectMembers.fetch(projectId);
  }, [projectId, projectMembers]);
  const membersLoaded = projectId != null && projectMembers.has(projectId);
  const projectUsers = projectId != null ? projectMembers.get(projectId) : [];
  const memberIds = new Set(projectUsers.map((u) => u.id));

  // WHA-3507: silently drop assignees without access to the issue's project,
  // once its member list has landed — an empty pre-fetch list would otherwise
  // clear valid selections. A correction, not input: a Gantt band's assignee
  // who is not in the draft's project must not make an untouched draft ask
  // for a description when it is closed.
  useEffect(() => {
    if (projectId == null || !membersLoaded) return;
    const allowed = new Set(projectMembers.get(projectId).map((u) => u.id));
    const current = valuesRef.current.assigneeIds;
    const next = current.filter((id) => allowed.has(id));
    if (next.length !== current.length) change({ assigneeIds: next }, { correction: true });
  }, [projectId, membersLoaded, projectMembers, change]);

  // People responsible for the chosen disciplines (Contacts) head the picker.
  const { responsibleFor } = useProjectResponsibilities(buildingId, projectId);
  const suggestedAssigneeIds = responsibleFor(values.disciplineIds).filter((uid) =>
    memberIds.has(uid)
  );

  const chipClass =
    "inline-flex min-w-0 items-center gap-1 truncate rounded border border-border px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground";

  return (
    <div ref={rootRef} className={cn("flex min-h-0 flex-1 flex-col", className)}>
      {/* Header — IssueDetailContent's strip: "New issue" where "#41" goes, the
          actions on the right. */}
      <div className="flex flex-shrink-0 items-center justify-between gap-2 px-5 py-4">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-[11px] font-medium text-muted-foreground">
            {headerLabel ?? t.pm.issues.draft.newIssue}
          </span>
        </div>
        <div className="flex flex-shrink-0 items-center gap-3">
          {(canAddChecklist || canMarkSubissue) && (
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      disabled={disabled}
                      className="h-6 w-6 text-muted-foreground hover:text-foreground"
                      aria-label={t.pm.issues.checklist.addToIssue}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>{t.pm.issues.checklist.addToIssue}</TooltipContent>
              </Tooltip>
              <DropdownMenuContent
                align="end"
                className="w-48"
                onCloseAutoFocus={(e) => {
                  if (!keepFocusOnClose.current) return;
                  keepFocusOnClose.current = false;
                  e.preventDefault();
                }}
              >
                {canAddChecklist && (
                  <DropdownMenuItem onSelect={openChecklist}>
                    <ListChecks className="mr-2 h-3.5 w-3.5" />
                    {t.pm.issues.checklist.addChecklist}
                  </DropdownMenuItem>
                )}
                {canMarkSubissue && (
                  <DropdownMenuItem onSelect={() => setParentPickerOpen(true)}>
                    <GitBranch className="mr-2 h-3.5 w-3.5" />
                    {t.pm.issues.detail.markAsSubissue}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {headerActions}
          {onClose && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  disabled={closeDisabled}
                  aria-label={t.common.close}
                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t.common.close}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <fieldset disabled={disabled} className="m-0 min-w-0 border-0 px-5 pb-4">
          {/* Title band — the only hairline, as in the panel. */}
          <div className="border-b py-2.5">
            {prefilledElement && (
              <div className="mb-1 flex max-w-full items-center gap-1">
                <span className={chipClass}>
                  <span>{t.pm.issues.linkingLabel}</span>
                  <span className="font-mono">{prefilledElement.element_type}</span>
                  <span className="truncate font-normal">
                    {prefilledElement.element_name ?? prefilledElement.element_guid}
                  </span>
                </span>
              </div>
            )}
            {parentIssue && (
              <div className="mb-1 flex max-w-full items-center gap-1">
                <span
                  className={chipClass}
                  title={t.pm.issues.detail.subissueOf
                    .replace("{n}", String(parentIssue.issue_number))
                    .replace("{title}", parentIssue.title)}
                >
                  ↑ #{parentIssue.issue_number} {parentIssue.title}
                </span>
                <button
                  type="button"
                  onClick={() => change({ parentIssueId: null })}
                  className="inline-flex shrink-0 items-center rounded border border-border px-1.5 py-0.5 text-muted-foreground hover:border-foreground/40 hover:text-foreground"
                  title={t.pm.issues.detail.makeTopLevel}
                  aria-label={t.pm.issues.detail.makeTopLevel}
                >
                  <Unlink className="h-3 w-3" />
                </button>
              </div>
            )}
            <textarea
              ref={titleRef}
              rows={1}
              value={values.title}
              onChange={(e) => change({ title: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !isImeEnter(e.nativeEvent)) {
                  e.preventDefault();
                  if (onTitleEnter) onTitleEnter();
                  else e.currentTarget.blur();
                } else if (e.key === "Escape" && onClose && !withinDialog) {
                  // The panel ignores Escape in text fields, and the title has
                  // focus from the start — so Escape here is the panel's close
                  // (a leave). In a dialog, Radix's own Escape closes it.
                  e.preventDefault();
                  onClose();
                }
              }}
              // People call the title the description; an empty one says so,
              // a shade lighter than the field's own text.
              placeholder={t.pm.issues.detail.description}
              aria-label={t.pm.issues.detail.description}
              aria-required="true"
              className={cn(ISSUE_TITLE_TEXTAREA_CLASS, "placeholder:text-muted-foreground/70")}
            />
          </div>

          <IssueFieldGrid
            orgUsers={projectUsers}
            assigneeIds={values.assigneeIds}
            onAssigneesChange={(assigneeIds) => change({ assigneeIds })}
            suggestedAssigneeIds={suggestedAssigneeIds}
            startDate={values.startDate}
            onStartDateChange={(startDate) => change({ startDate })}
            dueDate={values.dueDate}
            onDueDateChange={(dueDate) => change({ dueDate })}
            isOverdue={isDueDateOverdue(
              values.dueDate,
              !isOpenStatus(statusCatalogue, projectId, values.status)
            )}
            isDateRangeInverted={isDateRangeInverted({
              start_date: values.startDate || null,
              due_date: values.dueDate || null,
            })}
            status={values.status}
            buildingProjectId={projectId}
            onStatusChange={(status: IssueStatus) => change({ status })}
            readOnly={disabled}
          />

          {/* Tags (disciplines) — quiet outline pills with a "+" to add one. */}
          <div className="py-2.5">
            <PanelSectionLabel className="mb-1.5">{t.pm.issues.detail.tags}</PanelSectionLabel>
            <DisciplinePicker
              all={pmStore.disciplines}
              selectedIds={values.disciplineIds}
              onChange={(disciplineIds) => change({ disciplineIds })}
              emptyLabel={t.pm.issues.detail.addTag}
              badgeSize="md"
              badgeVariant="outline"
              addAffordance={!disabled}
              disabled={disabled}
              triggerClassName="-ml-1 min-h-8 max-w-full gap-1.5 px-1 py-0.5"
              fitLabels
              badgeRowClassName="gap-1.5"
            />
          </div>

          {values.checklistItems !== null && (
            <ChecklistDraftField
              items={values.checklistItems}
              onChange={(checklistItems) => change({ checklistItems })}
              onRemove={() => change({ checklistItems: null })}
              focusToken={checklistFocusToken}
            />
          )}

          {/* WHA-3347: documents linked once the issue exists. */}
          <IssueAttachmentsStager
            variant="panel"
            value={values.stagedAttachments}
            onChange={setStagedAttachments}
            buildingId={String(buildingId)}
            projectId={projectId != null ? String(projectId) : ""}
            targetFolderName={ISSUE_ATTACHMENTS_FOLDER_NAME}
            hideActions={disabled}
            onUploadingChange={onUploadingChange}
          />

          {error && <p className="py-2.5 text-sm text-destructive">{error}</p>}
        </fieldset>
      </div>

      {footer}

      <ParentIssuePickerDialog
        open={parentPickerOpen}
        onOpenChange={setParentPickerOpen}
        buildingId={buildingId}
        currentIssueId={null}
        onPicked={(parentIssueId) => change({ parentIssueId })}
      />
    </div>
  );
});
